import * as THREE from 'three/webgpu';
import { RevolveCutKeepBodyPreviewMesh } from '../../mesh/feature/revolve/RevolveCutKeepBodyPreviewMesh';
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent';

/**
 * 从 MeshData 构建 BufferGeometry。
 *
 * @param meshData 网格数据。
 * @returns Three.js 几何体。
 */
function buildBufferGeometryFromMeshData(meshData: MeshData): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(meshData.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(meshData.normals, 3));
    if (meshData.indices) {
        geometry.setIndex(new THREE.BufferAttribute(meshData.indices, 1));
    }
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    return geometry;
}

/**
 * 旋转切除保留实体高亮管理器。
 *
 * 文件职责：
 * 1. 将 worker 返回的 fragment mesh 转成 Three.js 高亮对象；
 * 2. 根据弹框草稿选择更新显隐；
 * 3. 统一释放几何体和材质，避免预览多次刷新后泄漏资源。
 */
export class RevolveCutKeepBodyPreviewManager {
    /** 场景引用。 */
    private readonly scene: THREE.Scene;
    /** 高亮网格映射。 */
    private readonly meshMap = new Map<string, RevolveCutKeepBodyPreviewMesh>();
    /** 共享高亮材质。 */
    private readonly material: THREE.MeshBasicMaterial;

    /**
     * @param scene 场景引用。
     */
    public constructor(scene: THREE.Scene) {
        this.scene = scene;
        this.material = new THREE.MeshBasicMaterial({
            color: 0xffc400,
            transparent: true,
            opacity: 0.46,
            /**
             * 保留实体高亮层是弹框选择的前景反馈，不参与真实实体遮挡判断；
             * 关闭深度测试可以彻底避免 fragment 与源实体共面时的 z-fighting。
             */
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide,
        });
    }

    /**
     * 设置可高亮的保留实体片段。
     *
     * @param fragmentMeshes 片段网格。
     */
    public setFragments(fragmentMeshes: RevolveCutPreviewFragmentMesh[]): void {
        this.clear();
        fragmentMeshes.forEach((fragmentMesh) => {
            const mesh = new RevolveCutKeepBodyPreviewMesh({
                sourceSolidId: fragmentMesh.sourceSolidId,
                shapeKey: fragmentMesh.shapeKey,
                geometry: buildBufferGeometryFromMeshData(fragmentMesh.meshData),
                material: this.material,
            });
            mesh.visible = false;
            this.meshMap.set(this.resolveKey(fragmentMesh.sourceSolidId, fragmentMesh.shapeKey), mesh);
            addObject3DToParent(mesh, this.scene);
        });
    }

    /**
     * 更新当前草稿选择对应的高亮状态。
     *
     * @param selection 按来源实体分组的片段键。
     */
    public updateSelection(selection: Record<string, string[]>): void {
        this.meshMap.forEach((mesh) => {
            mesh.visible = (selection[mesh.sourceSolidId] ?? []).includes(mesh.shapeKey);
        });
    }

    /**
     * 清空当前高亮网格。
     */
    public clear(): void {
        this.meshMap.forEach((mesh) => {
            if (mesh.parent) {
                mesh.parent.remove(mesh);
            }
            mesh.geometry.dispose();
        });
        this.meshMap.clear();
    }

    /**
     * 释放管理器资源。
     */
    public dispose(): void {
        this.clear();
        this.material.dispose();
    }

    /**
     * 构建映射键。
     *
     * @param sourceSolidId 来源实体 ID。
     * @param shapeKey 片段键。
     * @returns 映射键。
     */
    private resolveKey(sourceSolidId: string, shapeKey: string): string {
        return `${sourceSolidId}:${shapeKey}`;
    }
}
